import {MdFrameSpec, primaryDoorsItem} from "../../model/frame/MdFrameSpec";
import {frameOuterRect} from "./FrameOuterDimension";
import {TFrameBlock} from "./FrameBlockExt";
import {FrameBlockItem} from "./FrameBlockItem";
import {DimEntry, DimCollector, DimensionStack} from "../export/DimensionStack";
import type {FrameGridView} from "./FrameGridScene";
import {glassSize, liteGlass} from "../../model/glass/MdGlass";

// engineering-drawing dimensioning for the screen (frame) export. Reads the laid-out
// grid (all in one flat coordinate space) and collects every measurement — overall
// width/height, each leaf cell's width/height, each stick's thickness, and
// frame-mounted hardware offsets. Horizontal measurements draw stacked across the top,
// vertical ones down the left, and the hinge chain down the right. Replaces the
// inline, editable dimensions the interactive views draw.
export class ExportFrameDimensions {
    private view: FrameGridView;
    private stack: DimensionStack;

    constructor(view: FrameGridView) {
        this.view = view;
        this.stack = new DimensionStack(view.elev.draw);
    }

    draw(root: TFrameBlock, spec: MdFrameSpec) {
        const outer = frameOuterRect(root, spec);
        if(!outer) {
            this.stack.hide();
            return;
        }

        const horizontal = this.collectHorizontal(root, outer);
        const vertical = this.collectVertical(root, outer);
        const hardware = this.collectHardware(outer);
        const doorHardware = this.collectDoorHardware(root);
        const hinges = this.collectHinges(root);

        this.stack.draw({
            rect: outer,
            // re-attach to the current lines layer every draw: a grid rebuild swaps
            // parentThing.lines for a fresh Graphics, orphaning our container.
            parent: this.view.parentThing.lines,
            top: [...horizontal, ...hardware.horizontal, ...doorHardware.horizontal],
            left: [...vertical, ...hardware.vertical, ...doorHardware.vertical],
            right: hinges,
            clearance: this.view.anchorMarks.extentBeyond(outer),
        });
    }

    // hardware-only mode (the hardware export) keeps just the overall + door-cell
    // overall dimensions, dropping the other section sizes and stick thicknesses.
    private get hardwareOnly(): boolean {
        return this.view.elev.viewSettings.hardwareOnlyDimensions.value;
    }

    // horizontal measurements: overall width, each leaf cell's width, and each vertical
    // stick's thickness. In hardware-only mode only the overall + door-cell width.
    private collectHorizontal(root: TFrameBlock, outer: {x: number; width: number}): DimEntry[] {
        const c = new DimCollector();
        c.add(outer.x, outer.x + outer.width, outer.width);

        const cells = (root.getItems() as FrameBlockItem[]).filter(c => c.model.kind !== "threshold");
        const primary = primaryDoorsItem(this.view.elev.frameSpec);
        for(const cell of this.hardwareOnly ? cells.filter(c => c.model === primary) : cells) {
            const r = cell.layoutRect;
            if(!r) continue;
            c.add(r.x, r.x + r.width, r.width);

            // a lite cell is its own cut-out; add the glass width centred in it.
            if(cell.model.kind === "lite") {
                const gl = glassSize(r.width, r.height, liteGlass(cell.model));
                const gx = r.x + (r.width - gl.width) / 2;
                c.add(gx, gx + gl.width, gl.width);
            }
        }

        if(!this.hardwareOnly) {
            for(const s of this.view.sticks) {
                if(s.orientation !== "vertical") continue;
                const t = s.thickness.get();
                c.add(s.positionX, s.positionX + t, t);
            }
        }

        return c.entries();
    }

    // vertical measurements: overall height, each leaf cell's height, and each
    // horizontal stick's thickness. In hardware-only mode only the overall + door-cell
    // height. A doors cell measures its drawn leaf rather than its layoutRect — the
    // cell height is a layout value (it stops at the threshold, see MdFrameSpec's door
    // height), not the door size. The threshold cell is part of the door cell, not a
    // measurement of its own.
    private collectVertical(root: TFrameBlock, outer: {y: number; height: number}): DimEntry[] {
        const c = new DimCollector();
        c.add(outer.y, outer.y + outer.height, outer.height);

        const cells = (root.getItems() as FrameBlockItem[]).filter(c => c.model.kind !== "threshold");
        const primary = primaryDoorsItem(this.view.elev.frameSpec);
        for(const cell of this.hardwareOnly ? cells.filter(c => c.model === primary) : cells) {
            const leaf = cell.model.kind === "doors" ? cell.activeDoorLeafRect() : null;
            if(leaf) {
                c.add(leaf.top, leaf.top + leaf.height, leaf.height);
                continue;
            }
            const r = cell.layoutRect;
            if(!r) continue;
            c.add(r.y, r.y + r.height, r.height);

            // under a sloped head or sill the cell has a short end too.
            const short = cell.shortHeightSpan();
            if(short) c.add(r.y + short.y0, r.y + short.y1, short.y1 - short.y0);

            // a lite cell is its own cut-out; add the glass height centred in it.
            if(cell.model.kind === "lite") {
                const gl = glassSize(r.width, r.height, liteGlass(cell.model));
                const gy = r.y + (r.height - gl.height) / 2;
                c.add(gy, gy + gl.height, gl.height);
            }
        }

        if(!this.hardwareOnly) {
            for(const s of this.view.sticks) {
                if(s.orientation !== "horizontal") continue;
                const t = s.thickness.get();
                c.add(s.positionY, s.positionY + t, t);
            }
        }

        return c.entries();
    }

    // locate frame-mounted hardware. Each placement sits on a perimeter stick at a
    // known offset (hub = stick position + placement x/y). Dimension the axis that
    // matters for that stick: the height (down from the frame top, consistent with the
    // section-height dimensions) for a jamb/mullion, the horizontal position (from the
    // left edge) for a head/sill. Door-mounted hardware (handle/lock inside the door
    // cell) is not included here.
    private collectHardware(outer: {x: number; y: number; width: number; height: number}): {horizontal: DimEntry[]; vertical: DimEntry[]} {
        const horizontal: DimEntry[] = [];
        const vertical: DimEntry[] = [];

        const group = this.view.elev.hardwareGroup?.hardware ?? [];
        const byStick = new Map(this.view.sticks.map(s => [s.id, s]));

        for(const p of this.view.elev.hardware.list) {
            // only frame hardware that's actually drawn (its product is still in the group).
            if(p.target !== "frame") continue;
            if(!group.some(h => h.product === p.productId)) continue;

            const stick = p.stickId ? byStick.get(p.stickId) : undefined;
            if(!stick) continue;

            if(stick.orientation === "vertical") {
                const hubY = stick.positionY + p.y.get();
                if(hubY - outer.y > 1) vertical.push({lo: outer.y, hi: hubY, value: hubY - outer.y, lane: 0});
            } else {
                const hubX = stick.positionX + p.x.get();
                if(hubX - outer.x > 1) horizontal.push({lo: outer.x, hi: hubX, value: hubX - outer.x, lane: 0});
            }
        }

        return {horizontal, vertical};
    }

    // door-mounted hardware (handle/lock/etc.), dimensioned from the leaf edges like
    // the door export — horizontal from the leaf left, vertical from the leaf top —
    // rather than from the frame like collectHardware. Only for the hardware
    // elevation (the screen export omits door hardware); empty otherwise or when
    // there's no drawn door leaf.
    private collectDoorHardware(root: TFrameBlock): {horizontal: DimEntry[]; vertical: DimEntry[]} {
        const horizontal: DimEntry[] = [];
        const vertical: DimEntry[] = [];
        if(!this.hardwareOnly) return {horizontal, vertical};

        const primary = primaryDoorsItem(this.view.elev.frameSpec);
        const doorCell = (root.getItems() as FrameBlockItem[]).find(c => c.model === primary);
        const leaf = doorCell?.activeDoorLeafRect();
        if(!leaf) return {horizontal, vertical};

        // mirror the leaf face's crosshair filter: door placements whose product is
        // still in the group are the ones actually drawn (and now labeled). The hub
        // (p.x/p.y) is leaf-local, from the leaf top-left.
        const group = this.view.elev.hardwareGroup?.hardware ?? [];
        for(const p of this.view.elev.hardware.list) {
            if(p.target !== "door") continue;
            if(!group.some(h => h.product === p.productId)) continue;

            const x = p.x.get();
            const y = p.y.get();
            if(x > 1) horizontal.push({lo: leaf.left, hi: leaf.left + x, value: x, lane: 0});
            if(y > 1) vertical.push({lo: leaf.top, hi: leaf.top + y, value: y, lane: 0});
        }

        return {horizontal, vertical};
    }

    // the hinge chain, drawn on the right: from the bottom of the cross bar over the
    // door (the door cell's top edge) down to the top of the first hinge, then
    // top-of-hinge to top-of-hinge for the rest. All in frame-local coords: the door
    // cell's layoutRect gives the datum, the leaf top is topGap below it, and each
    // hinge's offsetTop is measured from that leaf top (its top edge is offsetTop −
    // half the hinge height). Empty when there are no hinges or no door cell.
    private collectHinges(root: TFrameBlock): DimEntry[] {
        const elev = this.view.elev;
        const hingeList = elev.hardware.hinges.list;
        if(hingeList.length === 0) return [];

        const primary = primaryDoorsItem(this.view.elev.frameSpec);
        const doorCell = (root.getItems() as FrameBlockItem[]).find(c => c.model === primary);
        if(!doorCell || !doorCell.layoutRect) return [];

        const datum = doorCell.layoutRect.y;
        const leafTop = datum + elev.doorSpec.topGap.get();
        const tops = hingeList
            .map((h, i) => leafTop + h.offsetTop.get() - elev.hardware.hinges.hingeHeightAt(i) / 2)
            .sort((a, b) => a - b);

        const entries: DimEntry[] = [];
        let prev = datum;
        for(const top of tops) {
            if(top - prev > 1) entries.push({lo: prev, hi: top, value: top - prev, lane: 0});
            prev = top;
        }
        return entries;
    }

    destroy() {
        this.stack.destroy();
    }
}
